<template>
  <StoryConversation :presentation="presentation" class="conversation-view">
    <!-- Error and loading banners -->
    <NotificationBanner
      v-if="error"
      :message="error"
      variant="error"
      :dismissible="true"
      :auto-dismiss="false"
      position="bottom"
      @dismiss="clearError"
    />

    <div
      v-if="initialLoading || sessionStore.readiness === 'loading'"
      class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 loading-banner"
    >
      Loading conversation...
    </div>

    <div v-else-if="sessionStore.readiness === 'error'" class="loading-banner">
      <p>{{ sessionStore.loadError || 'Failed to load conversation' }}</p>
      <button type="button" @click="sessionStore.initialize(Number(props.id))">Reload</button>
    </div>

    <!-- Chrome tools: the avatar opens the character drawer (an in-conversation
         peek, not navigation); the feather menu hosts the action panels. Both
         are teleported into the route-owned recessive chrome; the character
         name is the chrome title and ascent (the exit) lives there too. -->
    <template #identity-tools>
      <button
        class="conv-tools__character"
        type="button"
        aria-label="Character info"
        @click="characterDrawerOpen = true"
      >
        <BaseImage
          class="conv-tools__avatar"
          :src="displayCharacter.imgpath"
          :alt="displayCharacter.character_name"
          :image-focus="displayCharacter.img_focus"
        />
      </button>

    </template>

    <template v-if="conversation && !initialLoading && (sessionStore.readiness === 'ready' || sessionStore.readiness === 'degraded')" #composer-notices>
      <p v-if="sessionStore.saveBlocked" class="mb-2 text-sm text-center">Resolve the message save to continue, or choose a saved alternative.</p>
      <p v-if="sessionStore.recoveryStorageUnavailable" class="mb-2 text-sm text-center">A recovery copy could not be stored on this device. Keep this page open until the message is saved.</p>
      <p v-if="sessionStore.readiness === 'degraded'" class="mb-2 text-sm text-center">
        {{ sessionStore.resources.conversation?.bot_id != null && !sessionStore.resources.bot
          ? 'The selected bot is unavailable. Choose a replacement from the conversation menu.'
          : 'Some info failed to load — sending is disabled. Reload to retry.' }}
      </p>

    </template>

    <!-- Character drawer -->
    <CharacterDrawer
      v-if="character"
      :open="characterDrawerOpen"
      :character="character"
      @close="characterDrawerOpen = false"
    />

    <!-- Confirmation Dialog for Delete -->
    <ConfirmationDialog
      v-if="messageToDelete"
      :show="showConfirmDialog"
      :title="'Delete Message'"
      :message="'Are you sure you want to delete this message and all subsequent messages? This action cannot be undone.'"
      :confirm-text="'Delete'"
      :cancel-text="'Cancel'"
      variant="danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </StoryConversation>
</template>

<script setup lang="ts">
import { buildChatComposerStateFromDraft } from '@/features/story-conversation/composerState'
import StoryConversation from '@/features/story-conversation/components/StoryConversation.vue'
import type { StoryConversationPresentation } from '@/features/story-conversation/surface'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  RoleplayMessageRewriteService,
  useRoleplayConversationSessionStore,
  type DirectorNoteFromComposer,
} from '@/features/roleplay-conversation'
import { useBotsStore } from '@/features/bots/store'
import { useConversationComposer, conversationDraftKey } from '@/features/story-conversation/useConversationComposer'
import { parseErrorMessage } from '@/services/errorParser'
import CharacterDrawer from '@/features/roleplay-conversation/components/chat/CharacterDrawer.vue'
import BaseImage from '@/components/ui/BaseImage.vue'
import ConfirmationDialog from '@/components/ui/ConfirmationDialog.vue'
import NotificationBanner from '@/components/ui/NotificationBanner.vue'
import { usePageChrome } from '@/composables/usePageChrome'
import type { Bot, Character, Conversation, Persona } from '@/types/domain'
import type { MessageSlot, StoreMessage } from '@/lib/messageSlots'

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return parseErrorMessage(error.message)
  }
  if (typeof error === 'string' && error) {
    return parseErrorMessage(error)
  }
  if (error && typeof error === 'object') {
    const maybeMessage = (error as { message?: unknown }).message
    if (typeof maybeMessage === 'string' && maybeMessage) {
      return parseErrorMessage(maybeMessage)
    }
  }
  return fallback
}

const props = defineProps<{
  id: string
}>()

// Store instances
const sessionStore = useRoleplayConversationSessionStore()
const botsStore = useBotsStore()
const router = useRouter()

// Reactive state
let viewGeneration = 0
const initialLoading = ref<boolean>(false)
const error = ref<string>('')
const messageToDelete = ref<number | string | null>(null)
const showConfirmDialog = ref<boolean>(false)
const forkingMessageId = ref<number | string | null>(null)
const composer = useConversationComposer({
  draftKey: () => sessionStore.resources.conversation?.user_id
    ? conversationDraftKey(sessionStore.resources.conversation.user_id, 'roleplay', sessionStore.resources.conversation.id) : null,
  legacyDraftKey: () => sessionStore.resources.conversation ? String(sessionStore.resources.conversation.id) : null,
  rewriteBot: () => botsStore.defaultBot,
  canRewrite: () => sessionStore.readiness === 'ready' && !sessionStore.sending,
  conversationId: () => sessionStore.resources.conversation?.id ?? null,
  setError: (message) => { error.value = message },
  rewriteService: new RoleplayMessageRewriteService(),
})
const { visibleState: visibleComposerState, rewriteMode, isRewriteStreaming,
  rewriteBotName, canRewrite: canRewriteMessage, update: handleComposerStateUpdate,
  startRewrite: startMessageRewrite, restoreOriginal: restoreOriginalDraft, clearRewrite: clearRewriteState } = composer
const characterDrawerOpen = ref<boolean>(false)

// Computed properties
const activeBot = computed<Bot | null>(() => sessionStore.resources.bot)
const conversation = computed<Conversation | null>(() => sessionStore.resources.conversation)
const messages = computed<StoreMessage[]>(() => sessionStore.messages)
const slots = computed<MessageSlot[]>(() => sessionStore.slots)
const sending = computed<boolean>(() => sessionStore.sending)
const stopStreaming = (): void => {
  if (isRewriteStreaming.value) {
    composer.stopRewrite()
    return
  }
  sessionStore.stopStreaming()
}

// Check if there's any streaming message
const isConversationStreaming = computed<boolean>(() => {
  return messages.value.some((message) => message.phase === 'thinking' || message.phase === 'streaming')
})
const isStreaming = computed<boolean>(() => isConversationStreaming.value || isRewriteStreaming.value)

const composerMessagePlaceholder = 'Type a message'

const canSendMessage = computed<boolean>(() =>
  sessionStore.readiness === 'ready'
    && !sessionStore.saveBlocked
    && !isRewriteStreaming.value
    && Boolean(visibleComposerState.value.message?.trim?.()),
)

const character = computed<Character | null>(() => sessionStore.resources.character)
const displayCharacter = computed<Character>(() => character.value ?? ({
  character_name: 'Character unavailable',
  imgpath: '',
} as Character))

// Chrome (variant + recessive declared in route meta): the character's name is
// the title. A conversation is a terminal sink, so its exit is ascent up to the
// character's detail page — not browser history — resolved at runtime once the
// conversation loads (falling back to the Conversations list until then). The
// drawer-peek trigger and the action menu are teleported into the tools region.
usePageChrome({
  title: () => character.value?.character_name ?? null,
  ascentTo: () =>
    conversation.value?.character_id != null
      ? { name: 'character', params: { id: String(conversation.value.character_id) } }
      : { name: 'conversations' },
})

const persona = computed<Persona | null>(() => sessionStore.resources.persona)
const displayPersona = computed<Persona>(() => persona.value ?? ({
  persona_name: 'Persona unavailable',
  imgpath: '',
} as Persona))

const sendMessage = async (): Promise<void> => {
  if (isRewriteStreaming.value || !visibleComposerState.value.message.trim() || sessionStore.readiness !== 'ready') return
  const submission = composer.takeSubmission()
  const outcome = await sessionStore.send({ text: submission.text, directorNote: submission.directorNote })
  if (!outcome.ok && outcome.restoreComposer) {
    submission.restore()
    if (outcome.phase !== 'busy') error.value = 'Your message could not be sent — it has been restored to the composer. Please try again.'
  }
}

const initializeConversation = async (id: string): Promise<void> => {
  const generation = ++viewGeneration
  messageToDelete.value = null
  showConfirmDialog.value = false
  forkingMessageId.value = null
  characterDrawerOpen.value = false
  clearRewriteState({ abort: true })
  initialLoading.value = true
  error.value = ''

  try {
    await sessionStore.initialize(Number(id))
    if (generation !== viewGeneration) return
    if (sessionStore.loadError) error.value = sessionStore.loadError
  } catch (err: unknown) {
    if (generation === viewGeneration) error.value = getErrorMessage(err, 'Failed to load conversation')
  } finally {
    if (generation === viewGeneration) initialLoading.value = false
  }
}

onMounted(async () => {
  await initializeConversation(props.id)
})

watch(() => props.id, (nextId, priorId) => {
  if (nextId !== priorId) void initializeConversation(nextId)
})

onUnmounted(() => {
  viewGeneration++
  clearRewriteState({ abort: true })
  sessionStore.dispose()
})

watch(
  () => sessionStore.actionError,
  (message) => {
    if (message) error.value = message
  },
)

// Event handlers for message actions
const handleEdit = async (messageId: number | string, newContent: string): Promise<void> => {
  if (sessionStore.readiness !== 'ready') return
  try {
    await sessionStore.updateMessage(messageId, { content: newContent })
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to edit message')
    console.error('Failed to edit message:', err)
  }
}

const handleFork = async (messageId: number | string): Promise<void> => {
  if (sessionStore.readiness !== 'ready' || forkingMessageId.value !== null) return
  const generation = viewGeneration
  forkingMessageId.value = messageId
  error.value = ''

  try {
    const forkedConversation = await sessionStore.forkConversationAtMessage(messageId)
    if (generation !== viewGeneration || !forkedConversation) return
    await router.push({ name: 'conversation', params: { id: String(forkedConversation.id) } })
  } catch (err: unknown) {
    if (generation !== viewGeneration) return
    error.value = getErrorMessage(err, 'Failed to fork conversation')
    console.error('Failed to fork conversation:', err)
  } finally {
    if (generation === viewGeneration) forkingMessageId.value = null
  }
}

const handleReroll = async (messageId: number | string): Promise<void> => {
  await sessionStore.reroll(messageId)
}

const handleDelete = (messageId: number | string): void => {
  if (sessionStore.readiness !== 'ready') return
  messageToDelete.value = messageId
  showConfirmDialog.value = true
}

const handleResend = async (messageId: number | string): Promise<void> => {
  await sessionStore.resend(messageId)
}

const handleDirectorNoteUpdate = async (messageId: number | string, noteData: DirectorNoteFromComposer): Promise<void> => {
  try {
    await sessionStore.updateMessageDirectorNote(messageId, noteData)
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to update director note')
    console.error('Failed to update director note:', err)
  }
}

const handleDirectorNoteDelete = async (messageId: number | string): Promise<void> => {
  try {
    await sessionStore.deleteMessageDirectorNote(messageId)
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to delete director note')
    console.error('Failed to delete director note:', err)
  }
}

const confirmDelete = async (): Promise<void> => {
  if (!messageToDelete.value) return

  try {
    await sessionStore.deleteMessageAndSubsequent(messageToDelete.value)
  } catch (err: unknown) {
    error.value = getErrorMessage(err, 'Failed to delete message')
    console.error('Failed to delete message:', err)
  } finally {
    cancelDelete()
  }
}

const cancelDelete = (): void => {
  messageToDelete.value = null
  showConfirmDialog.value = false
}

// Clear error when notification banner is dismissed
const clearError = (): void => {
  error.value = ''
}

async function retryMessageSave(messageId: number | string) {
  const generation = viewGeneration
  const result = await sessionStore.retryMessageSave(messageId)
  if (!result || generation !== viewGeneration) return null
  if (result.status === 'saved') return { variant: 'success' as const, message: 'Message saved.' }
  if (result.status === 'deleted') return { variant: 'info' as const, message: 'This message has been deleted.' }
  if (result.status === 'failed') return { variant: 'error' as const, message: 'The save could not be confirmed. Your text is still kept here.' }
  const rejected = result.submission
  if (visibleComposerState.value.message || visibleComposerState.value.note) {
    return { variant: 'error' as const, message: 'The message was not saved. Clear the composer and retry to restore the original message.' }
  }
  composer.update(buildChatComposerStateFromDraft({
    text: rejected.originalText ?? rejected.request.content,
    directorNote: rejected.request.directorNote ? { ...rejected.request.directorNote } : null,
  }))
  sessionStore.releaseRejectedUserSave(rejected.request.requestId)
  return { variant: 'info' as const, message: 'The message was not saved. Your original text is back in the composer.' }
}

const presentation = computed<StoryConversationPresentation>(() => ({
  key: `roleplay:${props.id}`,
  active: Boolean(conversation.value && !initialLoading.value && (sessionStore.readiness === 'ready' || sessionStore.readiness === 'degraded')),
  streaming: isStreaming.value,
  readOnly: sending.value || sessionStore.readiness !== 'ready',
  accentName: character.value?.character_name,
  saveForSlot: (slot) => {
    const variant = slot.variants.find(item => String(item.id) === String(slot.activeVariantId))
    return variant?.saveState ? {
      busy: variant.saveState === 'saving', retry: () => retryMessageSave(slot.id),
      ...(slot.is_user_author ? { description: 'This message save could not be confirmed. Retry checks the same message; no response has been generated.' } : {}),
    } : null
  },
  timeline: {
    slots: slots.value, character: displayCharacter.value, persona: displayPersona.value,
    featuresForSlot: (slot) => slot.variants.some(variant => String(variant.id) === String(slot.activeVariantId) && variant.saveState)
      ? { edit: false, fork: false, reroll: false, resend: false, directorNotes: false } : {},
    loadingOlder: sessionStore.loadingOlder, hasMore: sessionStore.hasMore, loadOlder: sessionStore.loadOlder,
    alternatives: sessionStore.alternatives, forkDisabled: forkingMessageId.value !== null,
    forkingMessageId: forkingMessageId.value,
  },
  composer: {
    state: visibleComposerState.value, canSend: canSendMessage.value,
    disabled: sending.value || sessionStore.readiness !== 'ready' || sessionStore.saveBlocked, inputReadonly: isRewriteStreaming.value,
    canRewrite: canRewriteMessage.value, rewriteMode: rewriteMode.value, rewriteBotName: rewriteBotName.value,
    messagePlaceholder: composerMessagePlaceholder, notePlaceholder: 'Director note',
  },
  menu: { bot: activeBot.value, selectBot: sessionStore.setBot, disabled: sessionStore.sending },
  commands: {
    updateComposer: handleComposerStateUpdate, send: sendMessage, stop: stopStreaming,
    rewrite: startMessageRewrite, restoreOriginal: restoreOriginalDraft,
    edit: handleEdit, delete: handleDelete, fork: handleFork, reroll: handleReroll, resend: handleResend,
    selectVariant: sessionStore.selectVariant, updateDirectorNote: handleDirectorNoteUpdate,
    deleteDirectorNote: handleDirectorNoteDelete,
  },
}))
</script>

<style scoped lang="css">
/* ── Teleported chrome tools (avatar peek + action menu) ─────────────────────
   These live in #app-chrome-tools but keep this component's scoped data-v
   attribute, so these rules still apply after the teleport. */
.conv-tools__character {
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: background-color 150ms;
}

.conv-tools__character:hover {
  background: var(--theme-surface-interactive);
}

.conv-tools__avatar {
  width: 28px;
  height: 28px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
}

</style>
